import { getEmbeddedConnectedWallet, useWallets, ConnectedWallet, usePrivy } from '@privy-io/react-auth';

/**
 * Custom hook to interact with an embedded wallet using Privy.
 * It checks for disconnection and make sure the wallet is ready before attempting to use it.
 *
 * Usage: call `getEmbeddedWallet()` before attempting blockchain transactions.
 *
 * @returns {Object} An object containing the `getEmbeddedWallet` function.
 */
const useEmbeddedWallet = () => {
  const { ready: privyReady } = usePrivy();
  const { ready: walletReady, wallets } = useWallets();

  const waitForReady = async (retries = 3) => {
    while ((!walletReady || !privyReady) && retries > 0) {
      await new Promise((resolve) => setTimeout(resolve, 200));
      retries--;
    }
  };

  /**
   * Retrieves the connected embedded wallet.
   * @throws {Error} If the wallet cannot be retrieved.
   * @returns {Promise<ConnectedWallet>} The connected embedded wallet.
   */
  const getEmbeddedWallet = async (retryCount = 0) => {
    await waitForReady();

    console.log('getEmbeddedWallet, retryCount:', retryCount);
    console.log('Privy ready:', privyReady);
    console.log('wallets ready:', walletReady);
    let embeddedWallet = getEmbeddedConnectedWallet(wallets);

    console.log('wallets', wallets);
    if (!embeddedWallet) {
      // manual wallet look up
      embeddedWallet = wallets.find((acc) => acc.connectorType === 'embedded');
      console.log('found embedded wallet', embeddedWallet);
    }

    if (!embeddedWallet) {
      if (retryCount >= 3) throw new Error('Relogin to re-sync your wallet session.');
      // wait for 1.0 more second
      await waitForReady(5);
      return getEmbeddedWallet(retryCount + 1);
    }

    if (!(await embeddedWallet.isConnected())) {
      try {
        await embeddedWallet.loginOrLink();
      } catch (error) {
        console.error('Connect embedded wallet failed:', error);
      }
    }

    if (!embeddedWallet) throw new Error('Cannot connect to Privy in-game wallet.');

    return embeddedWallet;
  };

  return { getEmbeddedWallet };
};

export default useEmbeddedWallet;
